/**
 * @file src/pages/company-onboarding-wizard/components/JoinCompanyForm.jsx
 * @description Form for joining an existing company via invite code.
 *
 * What changed from the original:
 *  - isLoading now accepted as a prop from useOnboardingActions
 *    instead of managed locally — reflects the real API call state
 *  - Local isLoading state removed to avoid double loading states
 *  - Error state kept local — join errors are form-specific, not global
 *  - aria attributes added throughout
 *  - No console.log
 *
 * Rules:
 *  - No API calls — onJoin prop handles the API call in useOnboardingActions
 *  - Local state only for invite code input and error display
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { UserPlus, Shield, Key, ArrowRight, Loader2 } from "lucide-react";
import Input from "components/ui/Input";
import Button from "components/ui/Button";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_CODE_LENGTH = 6;
const MAX_CODE_LENGTH = 12;

// ---------------------------------------------------------------------------
// JoinCompanyForm
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   onJoin:    (inviteCode: string) => Promise<void>,
 *   isLoading: boolean,
 * }} props
 */
const JoinCompanyForm = ({ onJoin, isLoading }) => {
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");

  // ── Format invite code — uppercase hex only ───────────────────────────────
  const handleCodeChange = (value) => {
    const formatted = value
      .toUpperCase()
      .replace(/[^A-F0-9]/g, "")
      .slice(0, MAX_CODE_LENGTH);
    setInviteCode(formatted);
    if (error) setError(""); // Clear error on input change
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!inviteCode) {
      setError("Please enter an invite code");
      return;
    }
    if (inviteCode.length < MIN_CODE_LENGTH) {
      setError(`Invite code must be at least ${MIN_CODE_LENGTH} characters`);
      return;
    }

    setError("");

    try {
      await onJoin(inviteCode);
    } catch (err) {
      // onJoin re-throws with a clean message — display it here
      setError(err.message ?? "Failed to join. Please check your invite code.");
    }
  };

  const canSubmit = inviteCode.length >= MIN_CODE_LENGTH && !isLoading;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.25 }}
      className="max-w-md mx-auto"
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <UserPlus className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Join Existing Company
        </h2>
        <p className="text-gray-600 text-sm">
          Enter the invite code provided by your company administrator
        </p>
      </div>

      {/* ── Form ────────────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Invite code input */}
        <div className="space-y-2">
          <label
            htmlFor="invite-code"
            className="block text-sm font-medium text-gray-700"
          >
            Invite Code <span className="text-error">*</span>
          </label>

          <div className="relative">
            <Input
              id="invite-code"
              type="text"
              value={inviteCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="ABC123DEF456"
              disabled={isLoading}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-describedby={error ? "code-error" : "code-hint"}
              className={`
                text-center font-mono text-lg tracking-widest
                ${error ? "border-error" : ""}
                ${isLoading ? "opacity-60" : ""}
              `}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {isLoading ? (
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              ) : (
                <Key className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {error ? (
            <p id="code-error" className="text-sm text-error" role="alert">
              {error}
            </p>
          ) : (
            <p id="code-hint" className="text-xs text-gray-500">
              Invite codes are case-insensitive and contain letters and numbers
            </p>
          )}
        </div>

        {/* Submit button */}
        <Button type="submit" disabled={!canSubmit} className="w-full">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Joining Company...
            </>
          ) : (
            <>
              Join Company
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>

      {/* ── Security note ────────────────────────────────────────────────── */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-gray-500 shrink-0" />
          <div>
            <h4 className="font-medium text-gray-800 text-sm">Secure Access</h4>
            <p className="text-sm text-gray-600">
              Your invite code provides secure access to your company's
              ZATCA-compliant platform
            </p>
          </div>
        </div>
      </div>

      {/* ── Help text ────────────────────────────────────────────────────── */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          Don't have an invite code?{" "}
          <span className="text-primary font-medium">
            Contact your company administrator
          </span>
        </p>
      </div>
    </motion.div>
  );
};

export default JoinCompanyForm;
